import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { doc, getDocFromServer, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { VersionModal } from './components/VersionModal';
import DisplayRotativa from './components/DisplayRotativa';
import AdminDashboard from './components/AdminDashboard';
import Login from './components/Login';
import ErrorBoundary from './components/ErrorBoundary';

import AuraSoundscape from './components/AuraSoundscape';
import SuperAdmin from './components/SuperAdmin';
import Changelog from './components/Changelog';
import AuraAgent from './components/AuraAgent';
import HubResolver from './components/HubResolver';
import Support from './components/Support';
import { BusinessLanding } from './components/BusinessLanding';
import AuraViewer from './components/AuraViewer';

function MetadataHandler() {
  const location = useLocation();
  
  useEffect(() => {
    const isPathAdmin = location.pathname.startsWith('/admin');
    const isPathAura = location.pathname === '/aura';
    
    // Dynamic Content Config
    let title = 'Aura Business | Multimedia Hub';
    let description = 'Ecosistema de cartelería digital y diseño sonoro premium para negocios.';
    let image = 'https://solonet.es/wp-content/uploads/2026/03/LOGO-AURA-BUSINESS-512-x-512-px.png';

    if (isPathAdmin) {
      title = 'Aura Admin | Control Panel';
      description = 'Gestiona tus pantallas y sonido circadiano desde cualquier lugar.';
    } else if (isPathAura) {
      title = 'Aura Business | Donde la visibilidad digital se convierte en presencia física';
      description = 'Estar en internet es una opción; ser relevante en el mundo físico es una necesidad. Domina tu realidad local con Aura.';
      image = 'https://images.unsplash.com/photo-1534433832357-6c69807a62f1?auto=format&fit=crop&q=80&w=1200';
    }

    // 3. Update Title
    document.title = title;

    // 4. Update Meta Tags
    const updateMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    updateMeta('description', description);
    updateMeta('apple-mobile-web-app-title', isPathAdmin ? 'Aura Admin' : 'Aura Business');
    updateMeta('application-name', isPathAdmin ? 'Aura Admin' : 'Aura Business');

    // OG Tags
    updateMeta('og:title', title, 'property');
    updateMeta('og:description', description, 'property');
    updateMeta('og:image', image, 'property');
    updateMeta('og:url', window.location.href, 'property');

    // Twitter Tags
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', image);
    updateMeta('twitter:url', window.location.href);

  }, [location.pathname]);

  return null;
}

export default function App() {
  const [versionInfo, setVersionInfo] = useState<{ version: string; changelog: string } | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    async function testConnection() {
      try {
        await getDocFromServer(doc(db, 'test', 'connection'));
      } catch (error) {
        if(error instanceof Error && error.message.includes('the client is offline')) {
          console.error("Please check your Firebase configuration. ");
        }
      }
    }
    testConnection();

    // Version Monitoring
    const unSub = onSnapshot(doc(db, 'system_metadata', 'versioning'), (doc) => {
        if (doc.exists()) {
            const data = doc.data() as { version: string; changelog: string };
            const savedVersion = localStorage.getItem('app_version');
            
            if (savedVersion !== data.version) {
                setVersionInfo(data);
                setShowModal(true);
            }
        }
    });
    return () => unSub();
  }, []);

  const handleModalClose = () => {
    if (versionInfo) localStorage.setItem('app_version', versionInfo.version);
    setShowModal(false);
  };

  return (
    <ErrorBoundary>
      {showModal && versionInfo && (
        <VersionModal 
            version={versionInfo.version}
            changelog={versionInfo.changelog}
            onClose={handleModalClose}
        />
      )}
      <BrowserRouter>
        <MetadataHandler />
        <Routes>
          {/* Web Original (Home) */}
          <Route path="/" element={<AuraSoundscape />} />
          
          {/* Support Landing Page */}
          <Route path="/support" element={<Support />} />
          
          {/* Display Rotativa (Independiente) */}
          <Route path="/view" element={<AuraSoundscape />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/aura" element={<AuraViewer />} />
          <Route path="/admin/login" element={<Login />} />
          <Route path="/admin/super" element={<SuperAdmin />} />
          <Route path="/admin/changelog" element={<Changelog />} />
          <Route path="/negocio" element={<BusinessLanding />} />

          {/* Hub Slugs */}
          <Route path="/hub/:slug" element={<HubResolver />} />
          <Route path="/:slug" element={<HubResolver />} />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
